import { Dispatch, FC, SetStateAction, useState } from 'react';
import { FaPlay } from 'react-icons/fa';
import { FiArrowRight, FiCircle } from 'react-icons/fi';

import Button from 'molecules/Button/Button';

import FormPopup from 'components/Form/FormPopup';
import VideoPopup from 'components/Video/VideoPopup';

import type { ContentfulComponentCallToAction } from 'graphqlTypes';

interface ComponentButtonProps
  extends Omit<
    ContentfulComponentCallToAction,
    'image' | 'children' | 'contentful_id' | 'id' | 'internal' | 'node_locale'
  > {
  className?: string;
  isButton?: boolean;
  dataCy?: string;
  optimizelySelector?: string;
  handleClick?: () => void;
}

const variant = {
  Black: 'black',
  'Garden 300': 'primary',
  'Sunrise 500': 'secondary',
  'Outline White': 'outlined',
  'Outline Black': 'black-outlined',
  'Navy White': 'navy-outlined',
  'Outline Gray': 'gray-outlined',
  'Gray 900 Link': 'link',
  White: 'white',
  'White Link': 'white-link',
} as const;

type variantType = keyof typeof variant;

const getIcon = (icon?: string | null) => {
  switch (icon) {
    case 'Arrow Right':
      return <FiArrowRight />;
    case 'Circle':
      return <FiCircle />;
    case 'Play':
      return <FaPlay size={12} />;
    default:
      return null;
  }
};

const modalOnClick = (setIsOpen: Dispatch<SetStateAction<boolean>>) => {
  const modalClick = () => {
    setIsOpen(true);
  };

  return {
    onClick: modalClick,
  };
};

const ComponentButton: FC<ComponentButtonProps> = ({
  label,
  link,
  buttonColor,
  endIcon,
  className,
  isButton,
  popup,
  dataCy,
  handleClick,
  optimizelySelector,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const btnVariant = variant[buttonColor as variantType] || 'primary';
  const classNames = [`${btnVariant}-button button`, optimizelySelector, className].filter(Boolean).join(' ');

  let parsedHref = {};

  if (popup) {
    parsedHref = modalOnClick(setIsOpen);
  } else if (link) {
    parsedHref = { href: link };
  } else {
    parsedHref = { href: '' };
  }

  if (isButton) {
    parsedHref = {};
  }

  const referencePopup = () => {
    switch (popup?.__typename) {
      case 'ContentfulComponentForm':
        return <FormPopup {...popup} open={isOpen} onClose={() => setIsOpen(false)} />;
      case 'ContentfulComponentVideo':
        return isOpen && <VideoPopup {...popup} open={isOpen} onClose={() => setIsOpen(false)} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        {...parsedHref}
        endIcon={endIcon && getIcon(endIcon)}
        variant={btnVariant}
        className={classNames}
        data-cy={dataCy}
        data-optimizely={`button${otherProps?.contentful_id ? `-${otherProps?.contentful_id}` : ''}`}
        {...(handleClick && { onClick: () => handleClick() })}
        {...otherProps}
      >
        {label}
      </Button>
      {referencePopup()}
    </>
  );
};

export default ComponentButton;

ComponentButton.defaultProps = {
  label: 'Primary CTA',
  link: '/',
  buttonColor: 'Garden 300',
  endIcon: '',
};
